import { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import {
  InputGroup,
  FieldWrappers,
  Label,
  SubmitBtn,
  SideCancelBtn,
  Wrapper,
  ScrollableContent,
  ActionSection,
} from "../Styles/InputStyles";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import CategoryCard from "../CategoryCard";
import SelectItemModal from "../SelectItemModal";
import { useNavigate } from "react-router-dom";

const paymentSchema = Yup.object().shape({
  bankName: Yup.string()
    .matches(/^[A-Za-z0-9. -]+$/, "Only letters, numbers, spaces, and hyphens are allowed")
    .min(3, "Bank name must be at least 3 characters long")
    .required("Bank name is required"),
  accountHolderName: Yup.string()
    .matches(/^[A-Za-z ]+$/, "Only alphabets and spaces are allowed")
    .required("Account holder name is required"),
  accountNumber: Yup.string()
    .matches(/^\d{9,18}$/, "Enter a valid account number (9–18 digits)")
    .required("Account number is required"),
  ifscCode: Yup.string()
    .matches(/^[A-Za-z]{4}0[A-Z0-9a-z]{6}$/, "Enter a valid IFSC code")
    .required("IFSC code is required"),
  merchantId: Yup.string().nullable(),
  paymentGateway: Yup.string().required("Select a payment gateway"),
});

const paymentGatewayOptions = [
  { label: "Easebuzz", value: 3 },
  { label: "None", value: 0 },
];

const ManagePaymentDetails = ({
  handleClose,
  savePaymentMutate,
  savePaymentLoading,
  selectedData,
  leaseData,
}) => {
  const [height, setHeight] = useState(window.innerHeight);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [availableBanks, setAvailableBanks] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (leaseData?.landlordBanks && Array.isArray(leaseData.landlordBanks)) {
      setAvailableBanks(leaseData.landlordBanks);
      if (leaseData.landlordAccount && leaseData.landlordAccount !== false) {
        const existingBank = leaseData.landlordBanks.find(
          bank =>
            bank.accountNum === leaseData.landlordAccount.accountNum &&
            bank.bankName === leaseData.landlordAccount.bankName
        );
        if (existingBank) {
          setSelectedBank(existingBank);
        }
      }
    }
  }, [leaseData]);

  const initialValues = {
    bankName: selectedBank?.bankName || leaseData?.landlordAccount?.bankName || "",
    accountHolderName: selectedBank?.holderName || leaseData?.landlordAccount?.holderName || "",
    accountNumber: selectedBank?.accountNum || leaseData?.landlordAccount?.accountNum || "",
    ifscCode: selectedBank?.ifsc || leaseData?.landlordAccount?.ifsc || "",
    merchantId: leaseData?.landlordAccount?.mId || "",
    paymentGateway: leaseData?.landlordAccount?.gateway || "",
  };

  const handleSubmit = (values) => {
    const formatted = {
      ...values,
      landlordId: selectedData?.landlordId,
      propId: selectedData?.propId
    };
    savePaymentMutate(formatted);
  };

  const handleBankSelect = (bank) => {
    if (bank) {
      setSelectedBank(bank);
    } else {
      setSelectedBank(null);
    }
    setIsBankModalOpen(false);
  };

  const formatBankDisplay = (bank) => {
    if (!bank) return "";
    return `${bank.bankName} - ${bank.accountNum} (${bank.holderName})`;
  };

  const handleAddBank = () => {
    navigate('/landlord/bank-details', {
      state: {
        leaseDetails: leaseData?.leaseDetails?.landlordId,
      }
    });
  };

  return (
    <Container style={{ height: `${height}px`, overflow: "auto" }}>
      <Header>
        <Title>Manage Payment Details</Title>
        <button onClick={handleClose}>
          <MdClose color="white" size={24} />
        </button>
      </Header>

      <div className="px-6 h-full flex flex-col justify-between">
        <Formik
          initialValues={initialValues}
          validationSchema={paymentSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, setFieldValue }) => (
            <Form>
              <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                <Wrapper>
                  <InputGroup>
                    <div className="flex justify-between items-center">
                      <Label>Select Bank *</Label>
                      <EditButton
                        type="button"
                        onClick={handleAddBank}
                      >
                        Add Bank Account
                      </EditButton>
                    </div>
                    {availableBanks.length > 0 ? (
                      <>
                        <div onClick={() => setIsBankModalOpen(true)} className="cursor-pointer">
                          <CategoryCard
                            title="Bank"
                            selectedCategory={selectedBank}
                            displayFormat={formatBankDisplay}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex justify-center text-gray-500 text-sm h-12 items-center px-3 bg-gray-50 rounded border border-gray-200">
                          No banks found for this landlord.
                        </div>

                      </div>
                    )}
                  </InputGroup>

                  {/* <InputGroup>
                    <Label htmlFor="bankName">Bank Name *</Label>
                    <FieldWrappers className="bg-gray-100">
                      <Field
                        name="bankName"
                        type="text"
                        placeholder="Select a bank above"
                        autoComplete="off"
                        readOnly
                        value={selectedBank?.bankName || values.bankName}
                        className="w-full"
                      />
                    </FieldWrappers>
                    {errors.bankName && touched.bankName && (
                      <ErrorMessage>{errors.bankName}</ErrorMessage>
                    )}
                  </InputGroup> */}

                  <InputGroup>
                    <Label htmlFor="accountHolderName">
                      Account Holder Name *
                    </Label>
                    <FieldWrappers className="bg-gray-100">
                      <Field
                        name="accountHolderName"
                        type="text"
                        placeholder="Select a bank above"
                        autoComplete="off"
                        readOnly
                        value={selectedBank?.holderName || values.accountHolderName}
                        className="w-full"
                      />
                    </FieldWrappers>
                    {errors.accountHolderName && touched.accountHolderName && (
                      <ErrorMessage>{errors.accountHolderName}</ErrorMessage>
                    )}
                  </InputGroup>

                  <InputGroup>
                    <Label htmlFor="accountNumber">Account Number *</Label>
                    <FieldWrappers className="bg-gray-100">
                      <Field
                        name="accountNumber"
                        type="text"
                        placeholder="Select a bank above"
                        autoComplete="off"
                        readOnly
                        value={selectedBank?.accountNum || values.accountNumber}
                        className="w-full"
                      />
                    </FieldWrappers>
                    {errors.accountNumber && touched.accountNumber && (
                      <ErrorMessage>{errors.accountNumber}</ErrorMessage>
                    )}
                  </InputGroup>

                  <InputGroup>
                    <Label htmlFor="ifscCode">IFSC Code *</Label>
                    <FieldWrappers className="bg-gray-100">
                      <Field
                        name="ifscCode"
                        type="text"
                        placeholder="Select a bank above"
                        autoComplete="off"
                        readOnly
                        value={selectedBank?.ifsc || values.ifscCode}
                        style={{ textTransform: "uppercase" }}
                        className="w-full"
                      />
                    </FieldWrappers>
                    {errors.ifscCode && touched.ifscCode && (
                      <ErrorMessage>{errors.ifscCode}</ErrorMessage>
                    )}
                  </InputGroup>

                  <InputGroup>
                    <Label htmlFor="merchantId">Merchant ID (Optional)</Label>
                    <FieldWrappers>
                      <Field
                        name="merchantId"
                        type="text"
                        placeholder="Enter merchant ID"
                        autoComplete="off"
                      />
                    </FieldWrappers>
                    {errors.merchantId && touched.merchantId && (
                      <ErrorMessage>{errors.merchantId}</ErrorMessage>
                    )}
                  </InputGroup>

                  <InputGroup>
                    <Label htmlFor="paymentGateway">
                      Payment Gateway *
                    </Label>
                    <Select
                      name="paymentGateway"
                      id="paymentGateway"
                      className="w-full px-2"
                      value={values.paymentGateway}
                      onChange={(e) =>
                        setFieldValue("paymentGateway", e.target.value)
                      }
                      displayEmpty
                      style={{ height: "46px" }}
                    >
                      <MenuItem value="" disabled>
                        Select gateway
                      </MenuItem>
                      {paymentGatewayOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.paymentGateway && touched.paymentGateway && (
                      <ErrorMessage>{errors.paymentGateway}</ErrorMessage>
                    )}
                  </InputGroup>
                </Wrapper>
              </ScrollableContent>

              <ActionSection>
                <SideCancelBtn type="button" onClick={handleClose}>
                  Cancel
                </SideCancelBtn>
                <SubmitBtn
                  type="submit"
                  disabled={savePaymentLoading || !selectedBank}
                >
                  {savePaymentLoading ? "Saving..." : "Save"}
                </SubmitBtn>
              </ActionSection>

              {availableBanks.length > 0 && (
                <SelectItemModal
                  isOpen={isBankModalOpen}
                  onClose={() => setIsBankModalOpen(false)}
                  onSelect={handleBankSelect}
                  options={availableBanks}
                  selectedItem={selectedBank}
                  title="Select Bank"
                  searchPlaceholder="Search bank..."
                  idKey="id"
                  nameKey="bankName"
                  descriptionKeys={["accountNum", "holderName", "ifsc"]}
                  emptyMessage="No banks available"
                  noResultsMessage="No banks found"
                  displayFormat={formatBankDisplay}
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Container>
  );
};

const Header = tw.div`flex items-center justify-between bg-[#26A69A] py-4 px-4`;
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const Title = tw.h1`text-lg md:text-xl font-medium text-center text-white`;
const ErrorMessage = tw.div`text-red-500 text-xs mt-1`;
const EditButton = tw.button` text-sm px-1 py-0.5 items-center flex justify-between text-primary-color underline`;
export default ManagePaymentDetails;